"use client";
/* eslint-disable @next/next/no-sync-scripts */

import React, { useEffect, useState } from 'react';
import sectorService from '../../utils/services/sector';
import { GroupSector, Sector } from '@/types';
import { useKrpano } from '@/contexts/KrpanoProvider';
import { useLanguage } from '@/contexts/I18nProvider';
import { useTranslations } from 'next-intl';
import Link from 'next/link'
import Carousel from '../../components/Carousel/Carousel';
import MainFooter from '../../components/MainFooter/MainFooter';
import KrpanoViewer from '@/components/KrpanoViewer/KrpanoViewer';
import Script from 'next/script';
import Header from '../../components/Header/Header';
import { plusJakartaSans } from '@/app/layout';
import { useRouter } from 'next/navigation';

// Mapping từ id_field thực tế sang URL segment
const SECTOR_URL_MAPPING: Record<string, string> = {
  'farm': 'agriculture',
}

// Reverse mapping từ URL segment sang id_field thực tế
const SECTOR_URL_REVERSE_MAPPING: Record<string, string> = {
  'agriculture': 'farm',
}


const smartInteractionLabels: Record<string, string> = {
  vn: `TƯƠNG TÁC \nTHÔNG MINH 3D/360`,
  en: `3D/360 \nSMART INTERACTIVE`,
  ja: `3D/360 \nスマートインタラクティブ`,
  de: `3D/360 \nINTELLIGENTE INTERAKTIVE`,
  es: `3D/360 \nINTERACTIVO INTELIGENTE`,
  fr: `3D/360 \nINTERACTIF INTELLIGENT`,
  km: `3D/360 \nអន្តរកម្មឆ្លាតវៃ`,
  zh: `3D/360 \n智能互动`
};

import { isLocale } from '@/utils/constant/locales';

const getLocalizedUrl = (url: string, locale: string) => {
  if (!url) return '';
  
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      const parsed = new URL(url);
      if (parsed.hostname.includes('starglobal3d.com')) {
        let pathParts = parsed.pathname.split('/').filter(Boolean);
        if (isLocale(pathParts[0])) {
          pathParts.shift();
        }
        if (locale && locale !== 'vn') {
          pathParts.unshift(locale);
        }
        parsed.pathname = '/' + pathParts.join('/');
        return parsed.toString();
      }
    } catch (e) {
      // fallback
    }
  } else if (url.startsWith('/')) {
    let pathParts = url.split('/').filter(Boolean);
    if (isLocale(pathParts[0])) {
      pathParts.shift();
    }
    if (locale && locale !== 'vn') {
      pathParts.unshift(locale);
    }
    return '/' + pathParts.join('/');
  }
  return url;
};

export default function SectorPage({ groupSector, sector }: {
  groupSector: string,
  sector: string
}) {
  const router = useRouter();
  const [sectorData, setSectorData] = useState<Sector & { nameTranslate?: string; descTranslate?: string } | null>(null);
  const [prevSectorData, setPrevSectorData] = useState<Sector | null>(null);
  const [nextSectorData, setNextSectorData] = useState<Sector | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isScroll, setIsScroll] = useState<boolean>(false);
  const { krpano, isTurnOnSound, setIsTurnOnSound } = useKrpano()
  const { locale } = useLanguage();
  const t = useTranslations();
  const [smartInteractionLabel, setSmartInteractionLabel] = useState<string>(`TƯƠNG TÁC \nTHÔNG MINH 3D/360`);

  const getLocalizedPath = (path: string) => {
    if (!locale || locale === 'vn') return path;
    return `/${locale}${path}`;
  };

  useEffect(() => {
    setSmartInteractionLabel(smartInteractionLabels[locale] || smartInteractionLabels['vn']);
  }, [locale]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.clickFloatGroup = function (groupSector: string, sector: string) {
        // Map id_field to URL segment before navigating
        const urlSegment = SECTOR_URL_MAPPING[sector] || sector;
        const targetPath = `/smart-${urlSegment}-360`;
        const localizedPath = locale && locale !== 'vn' ? `/${locale}${targetPath}` : targetPath;
        router.push(localizedPath);
      };
    }
  }, [router, locale]);

  useEffect(() => {
    if (krpano) {
      if (typeof window !== 'undefined' && (window as any).stopAllKrpanoAudios) {
        (window as any).stopAllKrpanoAudios();
      }
      const urlSlug = sector.replace(/^smart-/, '').replace(/-360$/, '');
      // Map URL slug to actual id_field for krpano scene
      const originalSectorId = SECTOR_URL_REVERSE_MAPPING[urlSlug] || urlSlug;
      const sanitizedSectorId = originalSectorId.replace(/-/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
      krpano.call(`loadscene("scene_smart_${sanitizedSectorId}_3d", null, MERGE | KEEPVIEW,SLIDEBLEND(0.25, 0, linear))`)
    }
  }, [krpano, sector])

  useEffect(() => {
    const fetchSectorData = async () => {
      try {
        setIsLoading(true);
        // Extract id_field from URL pattern: smart-{id_field}-360
        const urlSlug = sector.replace(/^smart-/, '').replace(/-360$/, '');
        // Map URL slug to actual id_field
        const originalSectorId = SECTOR_URL_REVERSE_MAPPING[urlSlug] || urlSlug;
        const { data } = await sectorService.getAllNestedSectors();
        if (data && Array.isArray(data) && data.length > 0) {
          const allSectors = data.flatMap((group: any) =>
            group.fields.map((field: any) => ({ ...field, group_id: group.group_id }))
          );

          const currentIdx = allSectors.findIndex((item: any) => item.id_field === originalSectorId);

          if (currentIdx !== -1) {
            const sectorNewData = allSectors[currentIdx];
            const prevSectorData = allSectors[(currentIdx - 1 + allSectors.length) % allSectors.length];
            const nextSectorData = allSectors[(currentIdx + 1) % allSectors.length];
            if (sectorNewData) {
              setSectorData(sectorNewData);
              setPrevSectorData(prevSectorData);
              setNextSectorData(nextSectorData);
            }
          } else {
            console.error('Sector not found in any group:', sector);
          }
        } else {
          console.error('Invalid sector data received:', data);
        }
      } catch (error) {
        console.error('Error fetching sector data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSectorData();
  }, [sector, groupSector]);

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">
      <div>Loading...</div>
    </div>;
  }

  if (!sectorData) {
    return <div className="flex items-center justify-center min-h-screen">
      <div>No sector data found</div>
    </div>;
  }

  return (
    <>
      <Header hideFloatingButtons />


      <div className="w-full h-screen bg-center bg-cover">
        <div className='before:content-[""] before:absolute before:inset-0 before:z-10 before:bg-[rgba(0,0,0,0.5)] before:backdrop-blur-[2px] h-full'>
          <div className='relative z-10 flex flex-col h-full max-w-[100vw]'>

            {/* Toàn bộ Container chính có Blur nền */}
            <div className={`flex flex-col flex-auto min-h-0 backdrop-blur-[12px] bg-[#FFFFFF]/20 shadow-lg border-y border-white/20 ${plusJakartaSans.className}`}>

              {/* Header Row */}
              <div className="text-white shrink-0 mb-4 xl:mb-6 px-4 md:px-8 pt-20 md:pt-24 lg:pt-28">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-4 lg:gap-6 min-w-0">
                  <Link
                    href={getLocalizedPath('/')}
                    onClick={() => {
                      if (typeof window !== 'undefined') {
                        sessionStorage.setItem('returnFromSector', 'true');
                      }
                    }}
                  >
                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0 cursor-pointer hover:opacity-80 transition-opacity">
                      <path d="M19.1458 12.1436L20.56 10.7294L17.7316 7.90095L16.3173 9.31516L17.7316 10.7294L19.1458 12.1436ZM3.04672 22.5858C2.26567 23.3668 2.26567 24.6332 3.04672 25.4142C3.82777 26.1953 5.0941 26.1953 5.87515 25.4142L4.46094 24L3.04672 22.5858ZM17.7316 10.7294L16.3173 9.31516L3.04672 22.5858L4.46094 24L5.87515 25.4142L19.1458 12.1436L17.7316 10.7294Z" fill="white" />
                      <path d="M5.87515 22.5858C5.0941 21.8047 3.82777 21.8047 3.04672 22.5858C2.26568 23.3668 2.26568 24.6332 3.04672 25.4142L4.46094 24L5.87515 22.5858ZM16.3173 38.6848L17.7316 40.099L20.56 37.2706L19.1458 35.8564L17.7316 37.2706L16.3173 38.6848ZM4.46094 24L3.04672 25.4142L16.3173 38.6848L17.7316 37.2706L19.1458 35.8564L5.87515 22.5858L4.46094 24Z" fill="white" />
                      <path d="M4.46094 22C3.35637 22 2.46094 22.8954 2.46094 24C2.46094 25.1046 3.35637 26 4.46094 26V24V22ZM43.5413 26H45.5413V22H43.5413V24V26ZM4.46094 24V26H43.5413V24V22H4.46094V24Z" fill="white" />
                    </svg>
                  </Link>
                  {
                    locale === "en" ? (
                      <h1 className='text-xl sm:text-2xl font-bold uppercase md:text-3xl lg:text-4xl text-white break-words whitespace-pre-wrap md:whitespace-normal min-w-0 flex-1 leading-tight'>3D/360 SMART INTERACTIVE {sectorData.display_en.trim()}</h1>
                    ) : locale === "ja" ? (
                      <h1 className='text-xl sm:text-2xl font-bold uppercase md:text-3xl lg:text-4xl text-white break-words whitespace-pre-wrap md:whitespace-normal min-w-0 flex-1 leading-tight'>
                        {(sectorData.display_ja || sectorData.display_en).trim()} {smartInteractionLabel}
                      </h1>
                    ) : (
                      <h1 className='text-xl sm:text-2xl font-bold uppercase md:text-3xl lg:text-4xl text-white break-words whitespace-pre-wrap md:whitespace-normal min-w-0 flex-1 leading-tight'>
                        {sectorData.display_vn.trim()} {smartInteractionLabel}
                      </h1>
                    )
                  }
                </div>
              </div>

              {/* Content Row: Intro + Carousel */}
              <div className="flex flex-col md:flex-row flex-auto min-h-0 gap-6 md:gap-8 overflow-y-auto md:overflow-hidden">

                {/* Left Column: Intro */}
                <div className={`flex flex-col w-full md:w-[30%] xl:w-[25%] flex-shrink-0 h-auto md:h-full md:overflow-hidden pl-4 md:pl-8 pr-4`}>

                  {/* Intro Text & Brochure */}
                  <div className='flex flex-col gap-4 text-sm text-left leading-relaxed bg-[#00000080] rounded-xl p-4 md:p-6 h-fit max-h-full md:overflow-y-auto min-h-0 text-white'>
                    <div className="font-bold text-base uppercase text-[#FFF500]">
                      {locale === "en" ? "Sector Introduction:" : locale === "ja" ? "分野紹介:" : "Giới thiệu lĩnh vực:"}
                    </div>
                    <div className="font-normal whitespace-pre-line">
                      {locale === "en" ? (sectorData.description_en || sectorData.description_vn) : locale === "ja" ? (sectorData.description_ja || sectorData.description_en || sectorData.description_vn) : locale === "vn" ? sectorData.description_vn : (sectorData.description_en || sectorData.description_vn)}
                    </div>

                    {/* Voice button */}
                    <button
                      onClick={() => setIsTurnOnSound(prev => !prev)}
                      className="font-bold w-full justify-center text-xs md:text-sm text-white bg-[#00000080] hover:bg-[#00000099] px-4 py-3 rounded-full transition-colors duration-200 uppercase flex items-center gap-2 mt-2 outline-none focus:outline-none"
                    >
                      <svg className="size-5 shrink-0" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip_sound_icon_sector)">
                            {isTurnOnSound ? (
                                <>
                                    <path d="M9.70703 21.4994C8.60246 21.4994 7.70703 22.3948 7.70703 23.4994V40.5004C7.70703 41.605 8.60246 42.5004 9.70703 42.5004H21.7077L39.2086 60.0013V3.99854L21.7077 21.4994H9.70703Z" fill="currentColor"/>
                                    <path d="M46.21 17.8945C51.3902 20.4847 54.9604 25.8049 54.9604 32.0002C54.9604 38.1955 51.3902 43.5158 46.21 46.0709V17.8945Z" fill="currentColor"/>
                                </>
                            ) : (
                                <>
                                    <path d="M43.2373 23.9492L59.3399 40.0504" stroke="currentColor" strokeWidth="4"/>
                                    <path d="M59.3384 23.9492L43.2373 40.0504" stroke="currentColor" strokeWidth="4"/>
                                    <path d="M9.11523 21.4994C8.01066 21.4994 7.11523 22.3948 7.11523 23.4994V40.5004C7.11523 41.605 8.01066 42.5004 9.11523 42.5004H21.1159L38.6168 60.0013V3.99854L21.1159 21.4994H9.11523Z" fill="currentColor"/>
                                </>
                            )}
                        </g>
                        <defs>
                            <clipPath id="clip_sound_icon_sector">
                                <rect width="64" height="64" fill="currentColor"/>
                            </clipPath>
                        </defs>
                      </svg>
                      <span className="truncate">
                        {isTurnOnSound 
                            ? (locale === 'en' ? 'Turn off introduction' : locale === 'ja' ? '紹介音声をオフにする' : 'Tắt thuyết minh giới thiệu') 
                            : (locale === 'en' ? 'Turn on introduction' : locale === 'ja' ? '紹介音声をオンにする' : 'Bật thuyết minh giới thiệu')
                        }
                      </span>
                    </button>

                    {/* Brochure underneath Intro */}
                    <Link
                      href={getLocalizedUrl(sectorData.brochure_url, locale)}
                      target="_blank"
                      className="font-bold w-full justify-center text-sm md:text-base text-black bg-[#FFF500] hover:bg-white px-4 py-2 rounded-md transition-colors duration-200 uppercase flex items-center gap-2 mt-2"
                    >
                      {locale === "en" ? "VIEW BROCHURE" : locale === "ja" ? "パンフレットを見る" : "XEM BROCHURE"}
                      <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0 text-black">
                        <path d="M24 17.3333V25.3333C24 26.0406 23.719 26.7189 23.219 27.219C22.7189 27.719 22.0406 28 21.3333 28H6.66667C5.95942 28 5.28115 27.719 4.78105 27.219C4.28095 26.7189 4 26.0406 4 25.3333V10.6667C4 9.95942 4.28095 9.28115 4.78105 8.78105C5.28115 8.28095 5.95942 8 6.66667 8H14.6667M20 4H28M28 4V12M28 4L13.3333 18.6667" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </Link>
                  </div>

                </div>

                {/* Right Column: Carousel */}
                <div className="flex flex-col w-full md:w-[70%] xl:w-[75%] h-auto md:h-full min-h-[300px]">
                  <Carousel idField={sectorData.id_field} isScroll={isScroll} setIsScroll={setIsScroll} />
                </div>

              </div>

              {/* Footer Row (Prev/Next buttons) */}
              <div className='flex justify-between font-medium py-4 border-t border-white/30 shrink-0 px-4 md:px-8 text-white overflow-hidden'>
                {prevSectorData ? (
                  <Link href={getLocalizedPath(`/smart-${SECTOR_URL_MAPPING[prevSectorData?.id_field] || prevSectorData?.id_field}-360`)} className='flex items-center gap-2 cursor-pointer group'>
                    <button className='flex items-center gap-2 cursor-pointer group-hover:bg-[#00000059] transition-colors text-left bg-[#00000080] px-4 py-2 rounded-md'>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6 shrink-0">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
                      </svg>
                      <div className="flex flex-col uppercase">
                        <span className="font-bold text-sm sm:text-base">
                          {locale === 'en' ? prevSectorData?.display_en : locale === 'ja' ? (prevSectorData?.display_ja || prevSectorData?.display_en) : prevSectorData?.display_vn}
                        </span>
                      </div>
                    </button>
                  </Link>
                ) : <div />}

                {nextSectorData ? (
                  <Link href={getLocalizedPath(`/smart-${SECTOR_URL_MAPPING[nextSectorData?.id_field] || nextSectorData?.id_field}-360`)} className='flex items-center gap-2 cursor-pointer group'>
                    <button className='flex items-center gap-2 cursor-pointer group-hover:bg-[#00000059] transition-colors text-right bg-[#00000080] px-4 py-2 rounded-md'>
                      <div className="flex flex-col uppercase">
                        <span className="font-bold text-sm sm:text-base">
                          {locale === 'en' ? nextSectorData?.display_en : locale === 'ja' ? (nextSectorData?.display_ja || nextSectorData?.display_en) : nextSectorData?.display_vn}
                        </span>
                      </div>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6 shrink-0">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
                      </svg>
                    </button>
                  </Link>
                ) : <div />}
              </div>

            </div>

          </div>
        </div>
      </div>



      <KrpanoViewer />
      <Script
        src="https://sp.zalo.me/plugins/sdk.js"
        strategy="lazyOnload"
      />
      <script src={`${process.env.NEXT_PUBLIC_BASE_PATH_STATIC}/globalFunctions.js`}></script>




    </>


  );
}